/**
 * Фото на вход нейросети — правила подгонки (чистые функции, без DOM).
 *
 * Владелец 29.09: «универсальные обработчики на фронте по приёму правильных форматов для каждой нейронки,
 * чтобы юзер сразу мог понять, что ему грузить» и «не потерять качество, не исказить картинку и угодить
 * клиентам». Одобренные правила:
 *  1. Подходит — не трогаем: уходит исходный файл, без пересжатия.
 *  2. Не тот формат — меняем только упаковку: .jfif/.jpe → .jpg (те же байты); HEIC, AVIF, BMP, GIF,
 *     TIFF → JPEG 95 % (PNG, если исходник мог быть с прозрачностью).
 *  3. Больше максимума стороны — пропорционально уменьшаем.
 *  4. Меньше минимума — увеличиваем, если не хватает не больше чем вдвое; сильнее — отказ с объяснением
 *     (иначе клиент заплатит за размытое лицо).
 *  5. Вытянутее допустимого — никогда не растягиваем: срезать до 10 % картинки — сами, по центру;
 *     больше — клиент двигает рамку.
 *  6. Что сделали — одной строкой: пометка у превью и уведомление.
 *
 * Прод до правил (01.06–29.09): 252 «File type not supported» у Seedance 2.5 / Kling 2.6 / Gemini Omni —
 * с 25.09 все из-за .jfif; «Aspect ratio must be between 0.4 and 2.5» 48 и «Width must be between 300px
 * and 6000px» 20 у Seedance — фото из слотов тренда, последнего кадра, свапа и Omni шли без проверки.
 */
import type { Model } from '../../../data/models'
import { IMAGE_MAX_PX, IMAGE_MAX_RATIO, IMAGE_MIN_PX } from './constants'
import type { ImageLimits } from './referenceLimits'

/** Во сколько раз можно увеличить маленькое фото. Сильнее — нейросеть не разглядит детали. */
export const MAX_UPSCALE = 2
/** Какую долю картинки срезаем сами (по центру), когда фото вытянутее допустимого. Больше — решает клиент. */
export const AUTO_CROP_MAX_LOSS = 0.1
/** Потолок пикселей при перерисовке: мобильный Safari не рисует холст больше ~16,7 Мп, а нейросетям
 *  столько и не нужно — они работают в 1–4 Мп. Подходящий исходник уходит как есть, без потолка. */
export const MAX_CANVAS_PIXELS = 16_000_000

export type ImageFormat = 'jpeg' | 'png' | 'webp' | 'gif' | 'bmp' | 'heic' | 'avif' | 'tiff' | 'unknown'

/** Форматы, которые уходят без перекодирования. WebP сервер при загрузке сам переводит в JPEG/PNG. */
export const PASS_FORMATS: readonly ImageFormat[] = ['jpeg', 'png', 'webp']

const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1']

const ascii = (b: Uint8Array, from: number, to: number): string => String.fromCharCode(...b.subarray(from, to))

/** Настоящий формат по первым байтам: имя и MIME бывают чужими (.jfif у JPEG, HEIC под именем .jpg). */
export function sniffImageFormat(b: Uint8Array): ImageFormat {
	if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg'
	if (b.length >= 8 && b[0] === 0x89 && ascii(b, 1, 4) === 'PNG') return 'png'
	if (b.length >= 12 && ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 12) === 'WEBP') return 'webp'
	if (b.length >= 6 && (ascii(b, 0, 6) === 'GIF87a' || ascii(b, 0, 6) === 'GIF89a')) return 'gif'
	if (b.length >= 12 && ascii(b, 4, 8) === 'ftyp') {
		const brand = ascii(b, 8, 12)
		if (brand === 'avif' || brand === 'avis') return 'avif'
		if (HEIF_BRANDS.includes(brand)) return 'heic'
	}
	if (b.length >= 4 && ((b[0] === 0x49 && b[1] === 0x49 && b[2] === 0x2a && b[3] === 0) || (b[0] === 0x4d && b[1] === 0x4d && b[2] === 0 && b[3] === 0x2a))) return 'tiff'
	if (b.length >= 2 && b[0] === 0x42 && b[1] === 0x4d) return 'bmp'
	return 'unknown'
}

const EXT: Partial<Record<ImageFormat, string>> = { jpeg: 'jpg', png: 'png', webp: 'webp' }
const MIME: Partial<Record<ImageFormat, string>> = { jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' }

/** Имя и MIME по настоящему формату: «фото.jfif» → «фото.jpg», image/jpeg. null — менять нечего. */
export function canonicalName(name: string, type: string, format: ImageFormat): { name: string; type: string } | null {
	const ext = EXT[format]
	const mime = MIME[format]
	if (!ext || !mime) return null
	const dot = name.lastIndexOf('.')
	const cur = dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
	const sameExt = cur === ext || (format === 'jpeg' && cur === 'jpeg')
	if (sameExt && type === mime) return null
	const base = dot > 0 ? name.slice(0, dot) : name || 'photo'
	return { name: sameExt ? name : `${base}.${ext}`, type: mime }
}

export interface CropRect {
	x: number
	y: number
	w: number
	h: number
}

/** Рамки фото модели: свои — из кода адаптера (Model.imageLimits); у моделей с видео на входе (Kling Motion)
 *  — общие, как было в одиночном слоте; у остальных размеры не проверяем (только упаковка и вес). */
export function imageRulesFor(model: Pick<Model, 'imageLimits' | 'requiresVideo'> | null | undefined): ImageLimits | null {
	if (model?.imageLimits) return model.imageLimits
	if (model?.requiresVideo) return { minPx: IMAGE_MIN_PX, maxPx: IMAGE_MAX_PX, maxRatio: IMAGE_MAX_RATIO }
	return null
}

export type ImagePlan =
	| { kind: 'keep' }
	/** crop — какой кусок исходника взять (null — целиком), width×height — каким он станет. */
	| { kind: 'fix'; crop: CropRect | null; width: number; height: number }
	/** Вытянутое сильнее, чем режем сами: клиент выбирает, что оставить. */
	| { kind: 'ask_crop'; ratio: number }
	| { kind: 'reject'; message: string }

/** Минимальная обрезка до допустимой пропорции — по центру, по длинной стороне. */
export function centerCrop(w: number, h: number, maxRatio: number): CropRect {
	if (w >= h) {
		const cw = Math.min(w, Math.floor(h * maxRatio))
		return { x: Math.floor((w - cw) / 2), y: 0, w: cw, h }
	}
	const ch = Math.min(h, Math.floor(w * maxRatio))
	return { x: 0, y: Math.floor((h - ch) / 2), w, h: ch }
}

export function tooSmallMessage(w: number, h: number, minPx: number): string {
	return `Фото слишком маленькое (${w}×${h} px): этой нейросети нужно от ${minPx} px по каждой стороне. Загрузите фото крупнее — иначе лицо и детали выйдут размытыми.`
}

/**
 * Что сделать с фото w×h по рамкам модели. userCrop — рамка, которую выбрал клиент (уже нужной пропорции).
 * Без рамок модели размеры не трогаем: упаковку и вес решает вызывающий.
 */
export function planImage(w: number, h: number, rules: ImageLimits | null | undefined, userCrop?: CropRect | null): ImagePlan {
	if (!rules || !(w > 0) || !(h > 0)) return { kind: 'keep' }
	let crop: CropRect | null = userCrop ?? null
	let cw = crop ? crop.w : w
	let ch = crop ? crop.h : h

	if (Math.max(cw, ch) / Math.min(cw, ch) > rules.maxRatio + 1e-6) {
		const c = centerCrop(cw, ch, rules.maxRatio)
		// Сначала — хватит ли потом размера: не просим обрезать то, что всё равно не подойдёт.
		if (Math.min(c.w, c.h) * MAX_UPSCALE < rules.minPx) return { kind: 'reject', message: tooSmallMessage(w, h, rules.minPx) }
		const loss = 1 - (c.w * c.h) / (cw * ch)
		if (loss > AUTO_CROP_MAX_LOSS && !userCrop) return { kind: 'ask_crop', ratio: rules.maxRatio }
		crop = crop ? { x: crop.x + c.x, y: crop.y + c.y, w: c.w, h: c.h } : c
		cw = c.w
		ch = c.h
	}

	const short = Math.min(cw, ch)
	const long = Math.max(cw, ch)
	let scale = 1
	if (short < rules.minPx) {
		scale = rules.minPx / short
		if (scale > MAX_UPSCALE) return { kind: 'reject', message: tooSmallMessage(w, h, rules.minPx) }
	} else if (long > rules.maxPx) {
		scale = rules.maxPx / long
	}
	if (!crop && scale === 1) return { kind: 'keep' }

	let outW = Math.round(cw * scale)
	let outH = Math.round(ch * scale)
	if (scale > 1) {
		// Округление не должно увести короткую сторону ниже минимума.
		if (outW < rules.minPx) outW = rules.minPx
		if (outH < rules.minPx) outH = rules.minPx
	}
	return { kind: 'fix', crop, width: outW, height: outH }
}

/** Размер, до которого уменьшить при перерисовке, чтобы не превысить потолок пикселей (пропорционально). */
export function capPixels(w: number, h: number, maxPixels = MAX_CANVAS_PIXELS): { width: number; height: number } {
	if (w * h <= maxPixels) return { width: w, height: h }
	const k = Math.sqrt(maxPixels / (w * h))
	return { width: Math.max(1, Math.floor(w * k)), height: Math.max(1, Math.floor(h * k)) }
}

/**
 * Одна строка «что сделали» — по ФАКТИЧЕСКОМУ итогу (потолок пикселей мог ужать сильнее плана): пометка у превью
 * и уведомление. Смена одной упаковки (.jfif → .jpg, HEIC → JPEG) качества не меняет — о ней не пишем.
 */
export function describeFix(f: {
	cropped: boolean
	/** Размер куска исходника после обрезки. */
	fromW: number
	fromH: number
	/** Итоговый размер файла. */
	toW: number
	toH: number
	/** Пересжали только ради веса (размер тот же). */
	weightMb?: number
}): string | undefined {
	const notes: string[] = []
	if (f.cropped) notes.push('обрезали края')
	if (Math.min(f.toW, f.toH) > Math.min(f.fromW, f.fromH)) notes.push(`увеличили до ${f.toW}×${f.toH} px`)
	else if (Math.max(f.toW, f.toH) < Math.max(f.fromW, f.fromH)) notes.push(`уменьшили до ${f.toW}×${f.toH} px`)
	else if (f.weightMb) notes.push(`уменьшили вес до ${String(Math.round(f.weightMb * 10) / 10).replace('.', ',')} МБ`)
	return notes.length ? `Фото подогнали под нейросеть: ${notes.join(', ')}.` : undefined
}
